<template>
  <div>
    <v-progress-linear
      :active="loading"
      :indeterminate="loading"
      absolute
      top
    ></v-progress-linear>

    <v-breadcrumbs class="pa-4" :items="breadcrumb" large></v-breadcrumbs>

    <div v-if="!loading">
      <v-card class="ma-1">
        <v-card-title class="pb-1">{{ locationName }}</v-card-title>
        <v-card-text>{{ locationAddress }}</v-card-text>

        <v-chip
          style="margin-left: 16px;"
          v-for="state in locationStates"
          :key="state.value"
          :color="state.color"
          label
          outlined
          small
        >
          {{state.state}}
        </v-chip>

        <v-tabs style="margin-top: 16px;" show-arrows v-model="tab" class="mb-4">
          <v-tab
              v-for="tab in tabs"
              :key="tab.name"
              :disabled="
            tab.disabled ||
              (tab.name === 'publications' && !canMangeLocalPostsTag)
          "
          >
            {{ tab.title }}
          </v-tab>
        </v-tabs>
      </v-card>

      <v-tabs-items v-model="tab">
        <v-tab-item class="ma-0 pa-1">
          <location-info-tab />
        </v-tab-item>
        <v-tab-item class="ma-0 pa-1">
          <location-schedules-tab />
        </v-tab-item>
        <v-tab-item class="ma-0 pa-1">
          <location-historic-tab />
        </v-tab-item>
        <v-tab-item v-if="locationData" class="ma-0 pa-1">
          <location-photos-tab :locationData="locationData" />
        </v-tab-item>
        <v-tab-item class="ma-0 pa-1">
          <location-posts-tab />
        </v-tab-item>
        <v-tab-item class="ma-0 pa-1">
          <location-menus-tab />
        </v-tab-item>
      </v-tabs-items>
    </div>

    <v-snackbar v-model="snackbar.show" timeout="3000" :color="snackbar.color">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Ok
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { handleResponseError } from '@/api/handleError';
import LocationInfoTab from '@/views/Locations/Tabs/LocationInfoTab';
import LocationMenusTab from '@/views/Locations/Tabs/LocationMenusTab';
import LocationSchedulesTab from '@/views/Locations/Tabs/LocationSchedulesTab';
import LocationPostsTab from '@/views/Locations/Tabs/LocationPostsTab';
import LocationHistoricTab from '@/views/Locations/Tabs/LocationHistoricTab';
import LocationPhotosTab from '@/views/Locations/Tabs/LocationPhotosTab';
import { get } from 'lodash';
import hasPermission from '@/helpers/hasPermission';
import * as permissionsNames from '@/constants/permissions';

export default {
  name: 'Location',
  computed: {
    ...mapState({
      location: (state) => state.locations.location
    }),
    canMangeLocalPostsTag() {
      return hasPermission(permissionsNames.CAN_MANAGE_LOCAL_POSTS);
    }
  },
  components: {
    LocationInfoTab,
    LocationMenusTab,
    LocationSchedulesTab,
    LocationPostsTab,
    LocationHistoricTab,
    LocationPhotosTab,
  },
  created() {
    const id = this.$route.params.id;
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const tab = params.get('tab');
    this.tab = this.tabs.findIndex((t) => t.name === tab);

    if (!id) {
      this.showNotification('Nenhum local encontrado', 'red darken-2');
    } else {
      this.id = id;
      this.fetchLocation(id);
    }
  },
  beforeDestroy() {
    this.$store.dispatch('locations/clearFoodMenu')
    this.$store.dispatch('locations/clearLocation')
  },
  data: () => ({
    id: null,
    loading: false,
    locationData: null,
    locationName: '',
    locationAddress: '',
    locationStates: null,
    canHaveFoodMenus: false,
    breadcrumb: [
      {
        text: 'Locais',
        disabled: false,
        href: '/locations'
      },
      {
        text: 'Detalhes do Local',
        disabled: true,
        href: ''
      }
    ],
    tab: null,
    tabs: [
      { id: 1, title: 'Informações', name: 'info' },
      { id: 2, title: 'Agendamentos', name: 'schedules' },
      { id: 3, title: 'Histórico', name: 'historic' },
      { id: 4, title: 'Fotos', name: 'photos' },
      { id: 5, title: 'Publicações', name: 'publications' },
      { id: 6, title: 'Menu', name: 'menu' },
      { id: 7, title: 'Produtos', disabled: true, name: 'products' },
      { id: 8, title: 'Serviços', disabled: true, name: 'services' }
    ],
    snackbar: {
      text: '',
      show: false,
      color: 'info'
    }
  }),
  methods: {
    setLocationStatus(status) {
      const locationState = []

      Object.keys(status).map(function(key, value) {
        if(status[key]) {
          switch(key) {
            case 'isDuplicate':
              locationState.push({state: 'Duplicado', value: value, color: 'orange'});
              break;
            case 'isSuspended':
              locationState.push({state: 'Suspenso', value: value, color: 'red'});
              break;
            case 'needsReverification':
              locationState.push({state: 'Reverificação', value: value, color: 'blue'});
              break;
            case 'isPublished':
              locationState.push({state: 'Publicado', value: value, color: 'green'});
              break;
            case 'isDisconnected':
              locationState.push({state: 'Desconectado', value: value, color: 'blue'});
              break;
            case 'isLocalPostApiDisabled':
              locationState.push({state: 'Api desabilitada', value: value, color: 'blue'});
              break;
            case 'canModifyServiceList':
              locationState.push({state: 'Edição de serviços', value: value, color: 'blue'});
              break;
            case 'canHaveFoodMenus':
              locationState.push({state: 'Cardápios ativos', value: value, color: 'blue'});
              break;
            case 'hasPendingEdits':
              locationState.push({state: 'Edições pendentes', value: value, color: 'blue'});
              break;
            case 'hasPendingVerification':
              locationState.push({state: 'Em verificação', value: value, color: 'orange'});
              break;
            default:
              return
          }
        }
      });

      this.locationStates = locationState;
    },
    showNotification(message, color) {
      this.snackbar.text = message;
      this.snackbar.show = true;
      this.snackbar.color = color;
    },
    fetchLocation: async function(id) {
      this.loading = true;
      await this.$store.dispatch('locations/getLocation', id).catch((e) => {
        const message = handleResponseError(
          e,
          'Ocorreu um erro ao buscar o local.'
        );
        this.showNotification(message, 'red darken-2');
        this.loading = false;
      });
      this.loading = false;

      if (this.canHaveFoodMenus) {
        await this.fetchFoodMenu(this.id);
      }
    },
    fetchFoodMenu: async function(id) {
      this.loading = true;
      await this.$store.dispatch('locations/getFoodMenu', id).catch((e) => {
        const message = handleResponseError(
          e,
          'Ocorreu um erro ao buscar o menu.'
        );
        this.showNotification(message, 'red darken-2');
        this.loading = false;
      });
      this.loading = false;
    },
    changeUrlTab: async function(tab) {
      const url = new URL(window.location.href);
      let params = new URLSearchParams(url.search);
      if (tab === 'info') {
        params.delete('tab');
      } else {
        params.set('tab', tab);
      }
      const newUrl = window.location.pathname + '?' + params.toString();
      await this.$router.history.push(newUrl);
    }
  },
  watch: {
    $route(to) {
      this.tab = this.tabs.findIndex((t) => t.name === to.query.tab)
    },
    location(newVal) {

      this.locationData = newVal.data;
      this.locationName = get(newVal, 'data.location_name');
      this.locationAddress = get(newVal, 'data.address.full_address');
      this.canHaveFoodMenus = get(newVal, 'data.can_have_food_menus');
      this.setLocationStatus(newVal.data.state)
    },
    canHaveFoodMenus(value) {
      if (!value) {
        this.tabs = this.tabs.map((i) => {
          if (i.name === 'menu') {
            return { ...i, disabled: true };
          }
          return i;
        });
      } else {
        this.tabs = this.tabs.map((i) => {
          if (i.name === 'menu') {
            return { ...i, disabled: false };
          }
          return i;
        });
      }
    },
    tab(newTab, oldTab) {
      if ((oldTab !== null && newTab > 0) || newTab === 0 && oldTab > 0) {
        const tabName = this.tabs[newTab].name
        this.changeUrlTab(tabName)

        if (!this.location && this.id) {
          this.fetchLocation(this.id)
        }
      }
    }
  }
};
</script>